import { useRef, useState } from "react";
import type { Dispatch, SetStateAction, ReactNode } from "react";
import getViewportDimensions from "~/kernel/browser/getViewportDimensions";
import isBrowser from "~/kernel/browser/isBrowser";
import ReactModal from "react-modal";
import isNil from "~/kernel/validation/isNil";
import clamp from "~/kernel/numbers/clamp";
import RenderInBrowser from "./RenderInBrowser";

type ContextMenuProps = {
  clickEvent?: React.MouseEvent;
  children: ReactNode;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  position?: "cursor" | "below" | "bottomRight";
  anchor?: React.RefObject<HTMLElement>;
};

export default function ContextMenu(props: ContextMenuProps) {
  return (
    <RenderInBrowser fallback={<div></div>}>
      <BrowserContent {...props} />
    </RenderInBrowser>
  );
}

function BrowserContent({
  children,
  clickEvent,
  show,
  setShow,
  position = "cursor",
  anchor,
}: ContextMenuProps) {
  const { clientX: clickX, clientY: clickY } = clickEvent ?? {
    clientX: 0,
    clientY: 0,
  };
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const setSafePos = (
    position: { x: number; y: number },
    size: { width: number; height: number },
  ) => {
    const { width: viewWidth, height: viewHeight } = getViewportDimensions();
    const width = clamp(0, size.width, viewWidth);
    const height = clamp(0, size.height, viewHeight);
    const maxX = viewWidth - width;
    const maxY = viewHeight - height;
    const x = clamp(0, position.x, maxX);
    const y = clamp(0, position.y, maxY);
    return setPos({ x, y });
  };

  const requireAnchor = () => {
    const anchorEl = anchor?.current;

    if (isNil(anchorEl)) {
      throw new Error(`Anchor is required for position ${position}`);
    }

    return anchorEl;
  };

  if (isBrowser()) {
    return (
      <>
        <ReactModal
          appElement={document.getElementById("root") ?? undefined}
          ariaHideApp={false}
          isOpen={show}
          onRequestClose={() => {
            setShow(false);
          }}
          onAfterOpen={() => {
            const width = ref.current?.clientWidth ?? 0;
            const height = ref.current?.clientHeight ?? 0;

            switch (position) {
              case "cursor": {
                const x = clickX;
                const y = clickY;
                return setSafePos({ x, y }, { width, height });
              }
              case "below": {
                const anchorEl = requireAnchor();
                const anchorRect = anchorEl.getBoundingClientRect();
                const x = anchorRect.left;
                const y = anchorRect.bottom;
                return setSafePos({ x, y }, { width, height });
              }
              case "bottomRight": {
                const anchorEl = requireAnchor();
                const anchorRect = anchorEl.getBoundingClientRect();
                const x = anchorRect.left + anchorRect.width - width;
                const y = anchorRect.bottom;
                return setSafePos({ x, y }, { width, height });
              }
            }
          }}
          style={{ content: { left: `${pos.x}px`, top: `${pos.y}px` } }}
          overlayClassName={"fixed inset-0 z-20"}
          closeTimeoutMS={150}
          className={{
            base: "absolute top-1/3 left-0 max-w-xl -translate-y-3 opacity-[0] transition duration-150",
            afterOpen: "translate-y-0 opacity-[1]",
            beforeClose: "!-translate-y-3 !opacity-[0]",
          }}
        >
          <div ref={ref}>{children}</div>
        </ReactModal>
      </>
    );
  }

  return <div></div>;
}
